"use client";

import { useInView } from "motion/react";
import * as React from "react";
import { annotate } from "rough-notation";
import type { RoughAnnotation } from "rough-notation/lib/model";
import { cn } from "../../lib";
import { useFadeInContext } from "../custom/fade-in-on-scroll";

type AnnotationAction =
	| "highlight"
	| "underline"
	| "box"
	| "circle"
	| "strike-through"
	| "crossed-off"
	| "bracket";

export interface HighlighterProps {
	children: React.ReactNode;
	/**
	 * Use `div` when wrapping block-level content (e.g. full-width prompts).
	 * Default `span` keeps inline phrase highlights valid HTML.
	 */
	as?: "span" | "div";
	className?: string;
	action?: AnnotationAction;
	color?: string;
	strokeWidth?: number;
	animationDuration?: number;
	iterations?: number;
	padding?: number;
	multiline?: boolean;
	isView?: boolean;
	/** Extra delay (ms) after base readiness (fade + optional in-view) before showing the annotation. */
	deferMs?: number;
	/** Called once when the rough-notation annotation is shown (immediately after `annotation.show()`). */
	onShow?: () => void;
}

export function Highlighter({
	children,
	as = "span",
	className,
	action = "highlight",
	color = "#ffd1dc",
	strokeWidth = 1.5,
	animationDuration = 600,
	iterations = 2,
	padding = 2,
	multiline = true,
	isView = false,
	deferMs = 0,
	onShow,
}: HighlighterProps) {
	const elementRef = React.useRef<HTMLElement>(null);
	const onShowRef = React.useRef(onShow);
	onShowRef.current = onShow;
	const { isFadeInComplete } = useFadeInContext();

	const isInView = useInView(elementRef, {
		/** When gated by viewport, keep tracking so leaving/removing matches user expectation; otherwise latch after first intersect (perf). */
		once: !isView,
		margin: "-10%",
	});

	const baseReady = !isView || (Boolean(isInView) && isFadeInComplete);
	const [deferReady, setDeferReady] = React.useState(deferMs <= 0);

	React.useEffect(() => {
		if (deferMs <= 0) {
			setDeferReady(true);
			return;
		}
		if (!baseReady) {
			setDeferReady(false);
			return;
		}
		setDeferReady(false);
		const id = window.setTimeout(() => setDeferReady(true), deferMs);
		return () => clearTimeout(id);
	}, [baseReady, deferMs]);

	const shouldShow = baseReady && deferReady;

	React.useEffect(() => {
		if (!shouldShow) {
			return;
		}

		const element = elementRef.current;
		if (!element) {
			return;
		}

		const annotationConfig = {
			type: action,
			color,
			strokeWidth,
			animationDuration,
			iterations,
			padding,
			multiline,
		};

		const annotation: RoughAnnotation = annotate(element, annotationConfig);
		annotation.show();
		onShowRef.current?.();

		const disableSvgPointerEvents = () => {
			const svgSibling =
				element.nextElementSibling as SVGSVGElement | null;
			if (svgSibling?.tagName === "SVG") {
				svgSibling.style.pointerEvents = "none";
				return;
			}
			const parent = element.parentElement;
			if (parent) {
				const svgInParent = Array.from(parent.children).find(
					(child) => child.tagName === "SVG" && child !== element,
				) as SVGSVGElement | null;
				if (svgInParent?.style) {
					svgInParent.style.pointerEvents = "none";
				}
			}
		};

		disableSvgPointerEvents();
		setTimeout(disableSvgPointerEvents, 0);

		return () => {
			annotation.remove();
		};
	}, [
		shouldShow,
		action,
		color,
		strokeWidth,
		animationDuration,
		iterations,
		padding,
		multiline,
	]);

	const wrapperClassName = cn(
		as === "div"
			? "relative block w-full min-w-0 bg-transparent"
			: "relative inline bg-transparent align-baseline",
		className,
	);

	return React.createElement(
		as,
		{ ref: elementRef, className: wrapperClassName },
		children,
	);
}
